import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faSpinner } from '@/fontawesome';
import { formatDate, formatCurrencyCentValue, formatDelta, calculateRunningPosition } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError, showSuccessToast } from '@/utils/toasts';
import type { RecommendationSwingtrade, SwingTradeAdjustement, LongTermRecommendation, LongTermAdjustement } from '@/models';

interface SwingTradeAdjustementsTableProps {
  swingTrade: RecommendationSwingtrade | LongTermRecommendation;
  tradeType?: 'SWING' | 'LONGTERM';
  onEdit?: (adjustment: SwingTradeAdjustement | LongTermAdjustement) => void;
  onDelete?: (adjustmentId: string) => void;
  onSwingTradeUpdated?: (swingTrade: RecommendationSwingtrade | LongTermRecommendation) => void;
  showActions?: boolean;
}

export default function SwingTradeAdjustementsTable({
  swingTrade,
  tradeType = 'SWING',
  onEdit,
  onDelete,
  onSwingTradeUpdated,
  showActions = true
}: SwingTradeAdjustementsTableProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const currency = swingTrade.symbol?.currentData?.currency ?? 'USD';

  const handleDelete = async (adjustment: SwingTradeAdjustement | LongTermAdjustement) => {
    // Check if this is the last adjustment
    if (swingTrade.adjustements.length === 1) {
      handleAPIError(
        new Error('Der letzte Einstieg kann nicht gelöscht werden'),
        'Fehler beim Löschen'
      );
      return;
    }

    const confirmMessage = tradeType === 'SWING'
      ? `Einstieg wirklich löschen?\n\n` +
        `% des Depots: ${formatDelta((adjustment as SwingTradeAdjustement).delta)}\n` +
        `Preis: ${formatCurrencyCentValue(adjustment.price, currency)}\n` +
        `Zeitpunkt: ${formatDate(adjustment.timestamp)}\n\n` +
        `Diese Aktion kann nicht rückgängig gemacht werden.`
      : `Einstieg wirklich löschen?\n\n` +
        `Preis: ${formatCurrencyCentValue(adjustment.price, currency)}\n` +
        `Zeitpunkt: ${formatDate(adjustment.timestamp)}\n\n` +
        `Diese Aktion kann nicht rückgängig gemacht werden.`;

    const confirmed = window.confirm(confirmMessage);

    if (!confirmed) return;

    setDeletingId(adjustment.id);

    try {
      const updatedTrade = tradeType === 'SWING'
        ? await APIRoutes.deleteSwingTradeAdjustement(
            (swingTrade as RecommendationSwingtrade).id,
            (adjustment as SwingTradeAdjustement).id
          )
        : await APIRoutes.deleteLongTermTradeAdjustement(
            (swingTrade as LongTermRecommendation).id,
            (adjustment as LongTermAdjustement).id
          );
      if (onSwingTradeUpdated) {
        onSwingTradeUpdated(updatedTrade);
      }
      showSuccessToast('Einstieg erfolgreich gelöscht');
      if (onDelete) {
        onDelete(adjustment.id);
      }
    } catch (error) {
      handleAPIError(error, 'Fehler beim Löschen des Einstiegs');
    } finally {
      setDeletingId(null);
    }
  };

  // Sort adjustments by timestamp (ascending)
  const sortedAdjustments = [...swingTrade.adjustements].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Zeitstempel
            </th>
            {tradeType === 'SWING' && (
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                % des Depots
              </th>
            )}
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Preis
            </th>
            {tradeType === 'SWING' && (
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Laufende Position
              </th>
            )}
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Kommentar
            </th>
            {showActions && (
              <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Aktionen
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {sortedAdjustments.map((adjustment, index) => {
            const runningPosition = tradeType === 'SWING'
              ? calculateRunningPosition(sortedAdjustments as SwingTradeAdjustement[], index)
              : 0;
            const isDeleting = deletingId === adjustment.id;

            return (
              <tr key={adjustment.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {formatDate(adjustment.timestamp)}
                </td>
                {tradeType === 'SWING' && (
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <span className={`font-medium ${
                      (adjustment as SwingTradeAdjustement).delta >= 0
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {formatDelta((adjustment as SwingTradeAdjustement).delta)}
                    </span>
                  </td>
                )}
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {formatCurrencyCentValue(adjustment.price, currency)}
                </td>
                {tradeType === 'SWING' && (
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {(runningPosition * 100).toFixed(2)}%
                  </td>
                )}
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-xs whitespace-pre-wrap break-words">
                  {adjustment.notificationComment !== '' ? adjustment.notificationComment : <span className="text-gray-400 dark:text-gray-500 italic">-</span>}
                </td>
                {showActions && (
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm space-x-2">
                    <button
                      onClick={() => onEdit?.(adjustment)}
                      disabled={isDeleting}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer disabled:opacity-50"
                      title="Bearbeiten"
                    >
                      <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(adjustment)}
                      disabled={isDeleting || swingTrade.adjustements.length === 1}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      title={swingTrade.adjustements.length === 1 ? 'Letzter Einstieg kann nicht gelöscht werden' : 'Löschen'}
                    >
                      {isDeleting ? (
                        <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                      ) : (
                        <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                      )}
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        {/* Summary row - only for SWING trades which have currentPosition */}
        {tradeType === 'SWING' && (
          <tfoot className="bg-gray-100 dark:bg-gray-800 font-semibold">
            <tr>
              <td colSpan={3} className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                Total
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                {((swingTrade as RecommendationSwingtrade).currentPosition * 100).toFixed(2)}%
              </td>
              <td></td>
              {showActions && <td></td>}
            </tr>
          </tfoot>
        )}
      </table>

      {sortedAdjustments.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          Keine Einstiege vorhanden
        </div>
      )}
    </div>
  );
}
